import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import Loading from "../Common/Loading";

const initialState = {
  accountName: "",
  mode: "live",
  clientId: "",
  clientSecret: "",
  clientIdSandbox: "",
  clientSecretSandbox: "",
};

const PaypalAccountForm = ({
  setAccountList,
  updatingAccount,
  setUpdatingAccount,
  onSuccess,
  onCancel,
  embedded = false,
}) => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSandbox, setShowSandbox] = useState(false);

  const isUpdating = !!updatingAccount?._id;

  useEffect(() => {
    if (isUpdating) {
      setForm({
        accountName: updatingAccount.accountName || "",
        mode: updatingAccount.meta?.mode || "live",
        clientId: updatingAccount.apis?.live?.clientId || "",
        clientSecret: updatingAccount.apis?.live?.clientSecret || "",
        clientIdSandbox: updatingAccount.apis?.sandbox?.clientId || "",
        clientSecretSandbox: updatingAccount.apis?.sandbox?.clientSecret || "",
      });
      setShowSandbox(!!updatingAccount.apis?.sandbox?.clientId);
    } else {
      setForm(initialState);
      setShowSandbox(false);
    }
    setErrors({});
  }, [updatingAccount, isUpdating]);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const reset = () => {
    setForm(initialState);
    setShowSandbox(false);
    setErrors({});
    setUpdatingAccount({});
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      const requestParams = isUpdating
        ? {
            path: Enums.endpoints.paypal.ACCOUNT_PR,
            pathParams: { id: updatingAccount._id },
            type: "PUT",
            payload: form,
          }
        : {
            path: Enums.endpoints.paypal.ACCOUNT,
            type: "POST",
            payload: form,
          };

      const data = await Utils._apiRequest(requestParams);
      if (data?.errors) {
        setErrors(data.errors);
        toast.error(data.errors.message || "Lưu tài khoản thất bại");
      } else if (Array.isArray(data)) {
        setAccountList(data);
        toast.success(isUpdating ? "Đã cập nhật tài khoản" : "Đã thêm tài khoản PayPal");
        reset();
        if (typeof onSuccess === "function") onSuccess();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (e) => {
    if (e?.preventDefault) e.preventDefault();
    reset();
    if (typeof onCancel === "function") onCancel();
  };

  const formClass = embedded
    ? `position-relative${loading ? " loading" : ""}`
    : `my-3 p-3 p-md-4 border rounded-3 bg-body-tertiary${loading ? " loading" : ""}`;

  return (
    <Form onSubmit={submit} className={formClass}>
      <Loading show={loading} />

      <Form.Group className="mb-3">
        <Form.Label>Tên tài khoản</Form.Label>
        <Form.Control
          type="text"
          size="sm"
          placeholder="VD: PayPal Shop A"
          value={form.accountName}
          onChange={handleChange("accountName")}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Mode</Form.Label>
        <Form.Select size="sm" value={form.mode} onChange={handleChange("mode")}>
          <option value="live">Live</option>
          <option value="sandbox">Sandbox</option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Client ID (Live) {form.mode === "live" ? "(*)" : ""}</Form.Label>
        <Form.Control
          type="text"
          size="sm"
          placeholder="AY..."
          value={form.clientId}
          className={errors["live.clientId"] ? "is-invalid" : ""}
          onChange={handleChange("clientId")}
        />
        {errors["live.clientId"] && <p className="invalid-feedback">{errors["live.clientId"]}</p>}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Client Secret (Live) {form.mode === "live" ? "(*)" : ""}</Form.Label>
        <Form.Control
          type="password"
          size="sm"
          value={form.clientSecret}
          onChange={handleChange("clientSecret")}
        />
      </Form.Group>

      <hr className="mt-4" />

      <Form.Check
        type="switch"
        id="paypal-sandbox-switch"
        label="Lưu credentials Sandbox?"
        className="mb-3"
        onChange={(e) => setShowSandbox(e.target.checked)}
        checked={showSandbox}
      />

      {showSandbox && (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Client ID (Sandbox)</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              value={form.clientIdSandbox}
              className={errors["sandbox.clientId"] ? "is-invalid" : ""}
              onChange={handleChange("clientIdSandbox")}
            />
            {errors["sandbox.clientId"] && <p className="invalid-feedback">{errors["sandbox.clientId"]}</p>}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Client Secret (Sandbox)</Form.Label>
            <Form.Control
              type="password"
              size="sm"
              value={form.clientSecretSandbox}
              onChange={handleChange("clientSecretSandbox")}
            />
          </Form.Group>
        </>
      )}

      {errors.message && <div className="alert alert-danger py-2">{errors.message}</div>}

      <div className="d-flex justify-content-end gap-2 mt-2">
        {(embedded || isUpdating) && (
          <Button size="sm" variant="secondary" onClick={handleCancel} disabled={loading}>
            {isUpdating ? "Huỷ" : "Đóng"}
          </Button>
        )}
        <Button type="submit" size="sm" variant="primary" disabled={loading}>
          {isUpdating ? "Cập nhật" : "Kết nối PayPal"}
        </Button>
      </div>
    </Form>
  );
};

export default PaypalAccountForm;
